"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = __importDefault(require("lodash"));
const react_1 = __importStar(require("react"));
const ImplicitFilterBuilder_1 = __importDefault(require("../ImplicitFilterBuilder"));
const DashboardUtils = __importStar(require("./DashboardUtils"));
const WidgetFactory_1 = __importDefault(require("../widgets/WidgetFactory"));
const WidgetScoper_1 = __importDefault(require("../widgets/WidgetScoper"));
const ReactElementPrinter_1 = __importDefault(require("@mwater/react-library/lib/ReactElementPrinter"));
const LayoutManager_1 = __importDefault(require("../layouts/LayoutManager"));
const WidgetScopesViewComponent_1 = __importDefault(require("../widgets/WidgetScopesViewComponent"));
const layoutOptions_1 = require("./layoutOptions");
const WidgetComponent_1 = require("./WidgetComponent");
const vectorMaps_1 = require("../maps/vectorMaps");
const WidgetDataSourcePrioritizer_1 = require("./WidgetDataSourcePrioritizer");
const expressions_ui_1 = require("@mwater/expressions-ui");
/**
 * Displays a dashboard, handling removing of widgets. No title bar or other decorations.
 * Handles scoping and stores the state of scope
 */
const DashboardViewComponent = react_1.default.forwardRef((props, ref) => {
    const [widgetScoper, setWidgetScoper] = (0, react_1.useState)(new WidgetScoper_1.default());
    const widgetComps = (0, react_1.useRef)({});
    const [forceUpdate, setForceUpdate] = (0, react_1.useState)(0);
    const widgetDataSourcePrioritizer = (0, react_1.useMemo)(() => {
        return new WidgetDataSourcePrioritizer_1.WidgetDataSourcePrioritizer(props.dashboardDataSource, 10);
    }, [props.dashboardDataSource]);
    (0, react_1.useEffect)(() => {
        return () => {
            // Clear queue on component unmount
            widgetDataSourcePrioritizer.cancel();
        };
    }, [widgetDataSourcePrioritizer]);
    (0, react_1.useEffect)(() => {
        if (props.initialTOCEntryScroll) {
            // Getting heights of widgets properly requires a 0 length timeout
            setTimeout(() => {
                handleScrollToTOCEntry(props.initialTOCEntryScroll.widgetId, props.initialTOCEntryScroll.entryId);
            }, 0);
        }
        // Add listener to localstorage to update clipboard display
        window.addEventListener("storage", handleStorageChange);
        return () => {
            // Remove listener
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [props.initialTOCEntryScroll]);
    const handleStorageChange = () => {
        setForceUpdate(forceUpdate + 1);
    };
    const handleScopeChange = (id, scope) => {
        setWidgetScoper(widgetScoper.applyScope(id, scope));
    };
    const handleRemoveScope = (id) => {
        setWidgetScoper(widgetScoper.applyScope(id, null));
    };
    const handleItemsChange = (items) => {
        const design = lodash_1.default.extend({}, props.design, { items });
        props.onDesignChange(design);
    };
    const handleClipboardChange = (block) => {
        try {
            // If empty, just set it
            if (!block) {
                window.localStorage.removeItem("DashboardViewComponent.clipboard");
                setForceUpdate(forceUpdate + 1);
                return;
            }
            // Determine which tables are used (just peek for any uses of the table name. Not ideal, but easy)
            const tables = lodash_1.default.pluck(lodash_1.default.filter(props.schema.getTables(), (table) => JSON.stringify(block).includes(JSON.stringify(table.id))), "id");
            // Store in clipboard
            window.localStorage.setItem("DashboardViewComponent.clipboard", JSON.stringify({ block, tables }));
            setForceUpdate(forceUpdate + 1);
        }
        catch (err) {
            return alert(T `Clipboard not available`);
        }
    };
    const getClipboardContents = () => {
        try {
            return JSON.parse(window.localStorage.getItem("DashboardViewComponent.clipboard") || "null");
        }
        catch (err) {
            return null;
        }
    };
    const print = async () => {
        // Temporarily enable print mode for vector maps
        try {
            (0, vectorMaps_1.setPrintingModeEnabled)(true);
            // Create element at 1080 wide (use as standard printing width)
            const elem = (react_1.default.createElement("div", { style: { width: 1080 } },
                react_1.default.createElement(DashboardViewComponent, { ...props, onDesignChange: undefined, printMode: true })));
            const printer = new ReactElementPrinter_1.default();
            await printer.print(elem, { delay: 5000 });
        }
        finally {
            (0, vectorMaps_1.setPrintingModeEnabled)(false);
        }
    };
    (0, react_1.useImperativeHandle)(ref, () => ({
        print
    }));
    const getCompiledFilters = () => {
        let compiledFilters = DashboardUtils.getCompiledFilters(props.design, props.schema, DashboardUtils.getFilterableTables(props.design, props.schema));
        compiledFilters = compiledFilters.concat(props.filters || []);
        return compiledFilters;
    };
    const getTOCEntries = (layoutManager) => {
        const entries = [];
        for (let { id, type, design } of layoutManager.getAllWidgets(props.design.items)) {
            const widget = WidgetFactory_1.default.createWidget(type);
            // Add widgetId to each one
            for (let entry of widget.getTOCEntries(design, props.namedStrings)) {
                entries.push(lodash_1.default.extend({}, entry, { widgetId: id }));
            }
        }
        return entries;
    };
    const handleScrollToTOCEntry = (widgetId, entryId) => {
        const widgetComp = widgetComps.current[widgetId];
        if (!widgetComp) {
            return;
        }
        // Call scrollToTOCEntry if present
        return widgetComp.scrollToTOCEntry?.(entryId);
    };
    const renderScopes = () => {
        return (react_1.default.createElement(WidgetScopesViewComponent_1.default, { scopes: widgetScoper.getScopes(), onRemoveScope: handleRemoveScope }));
    };
    /** Locale that the dashboard design is in */
    const designLocale = props.design.locale ?? "en";
    /** Locale to display the dashboard in. If editing, always use design locale. */
    const displayLocale = props.onDesignChange != null ? designLocale : (props.locale ?? designLocale ?? "en");
    /** Translate function to use for display. Do not use when editing. */
    const translate = (0, react_1.useCallback)((input) => {
        if (designLocale === displayLocale) {
            return input;
        }
        return props.design.translations?.[displayLocale]?.[input] ?? input;
    }, [props.design.translations, designLocale, displayLocale]);
    const compRef = (widgetId, comp) => {
        return (widgetComps.current[widgetId] = comp);
    };
    let cantPasteMessage = "";
    const layoutManager = LayoutManager_1.default.createLayoutManager(props.design.layout);
    const compiledFilters = getCompiledFilters();
    // Get filterable tables
    const filterableTables = DashboardUtils.getFilterableTables(props.design, props.schema);
    // Determine toc entries
    const tocEntries = getTOCEntries(layoutManager);
    // Get clipboard contents
    const clipboardContents = getClipboardContents();
    // Check if can't paste because of missing table
    if (clipboardContents && !lodash_1.default.all(clipboardContents.tables, (table) => props.schema.getTable(table))) {
        cantPasteMessage = T `Dashboard is missing one or more data sources needed for the copied item.`;
    }
    const renderWidget = (options) => {
        const widgetDataSource = widgetDataSourcePrioritizer.getWidgetDataSource(options.type, options.id, options.priority ?? 0);
        // Get filters (passed in plus dashboard widget scoper filters)
        let filters = compiledFilters.concat(widgetScoper.getFilters(options.id));
        // Extend the filters to include implicit filters (filter children in 1-n relationships)
        if (props.design.implicitFiltersEnabled || props.design.implicitFiltersEnabled == null) {
            // Default is true
            const implicitFilterBuilder = new ImplicitFilterBuilder_1.default(props.schema);
            filters = implicitFilterBuilder.extendFilters(filterableTables, filters);
        }
        const widgetElem = (react_1.default.createElement(WidgetComponent_1.WidgetComponent, { key: options.id, type: options.type, schema: props.schema, dataSource: props.dataSource, widgetDataSource: widgetDataSource, design: options.design, scope: widgetScoper.getScope(options.id), filters: filters, onScopeChange: handleScopeChange.bind(null, options.id), onDesignChange: options.onDesignChange, width: options.width, height: options.height, onRowClick: props.onRowClick, namedStrings: props.namedStrings, tocEntries: tocEntries, onScrollToTOCEntry: handleScrollToTOCEntry, 
            // Keep references to widget elements
            widgetRef: compRef.bind(null, options.id), refreshKey: props.refreshKey, 
            // Use locale from props, design, or default to en
            locale: displayLocale, translate: translate }));
        return widgetElem;
    };
    const style = {
        height: "100%",
        position: "relative"
    };
    if (!props.printMode) {
        // Prevent this block from taking up too much space. Scrolling handled by layout manager.
        // Setting overflow-x stops the inner div from becoming too tall
        style.overflowX = "auto";
    }
    // Render widget container
    return (react_1.default.createElement(expressions_ui_1.LocaleContext.Provider, { value: displayLocale },
        react_1.default.createElement("div", { style: style },
            !props.hideScopes ? renderScopes() : undefined,
            layoutManager.renderLayout({
                items: props.design.items,
                onItemsChange: props.onDesignChange != null ? handleItemsChange : undefined,
                layoutOptions: (0, layoutOptions_1.getLayoutOptions)(props.design),
                renderWidget,
                clipboard: clipboardContents?.block,
                onClipboardChange: handleClipboardChange,
                cantPasteMessage
            }))));
});
exports.default = DashboardViewComponent;
